import { useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/features/auth';

/* ─────────────────────────────────────────────────────────────────────────────
   InviteLandingPage
   Handles two URL shapes:
     • /invite?token=<uuid>           (legacy / email-link format)
     • /join?company=<uuid>           (new share-link format)
     • /join/<uuid>                   (permanent route via JoinPage, handled separately)

   Logic:
     1. Extract the token from the URL.
     2. Persist it in sessionStorage (key: INVITE_TOKEN_KEY) so auth pages can
        pick it up after a redirect.
     3. If user is already logged-in  → navigate directly to /join/<token>.
     4. If user is not logged-in      → navigate to /signup?invite=<token>
        (SignUpPage will read the token from query-string OR sessionStorage).
────────────────────────────────────────────────────────────────────────────── */

/** Key used across the auth flow to persist the pending invite token */
export const INVITE_TOKEN_KEY = 'axelio_pending_invite_token';

export function InviteLandingPage() {
  const [searchParams] = useSearchParams();
  // Support /join/:inviteToken as well (though JoinPage handles it separately)
  const { inviteToken: paramToken } = useParams<{ inviteToken?: string }>();
  const navigate  = useNavigate();
  const { user, loading } = useAuth();

  // Resolve token from all possible locations:
  //   ?token=<uuid>   (legacy /invite?token=… links)
  //   ?company=<uuid> (new /join?company=… share links)
  //   /:inviteToken   (URL segment, handled by JoinPage but covered here as fallback)
  const token =
    searchParams.get('token') ||
    searchParams.get('company') ||
    paramToken ||
    '';

  useEffect(() => {
    if (loading) return; // wait for Supabase auth to resolve

    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    // Always persist the token so auth pages can find it after any redirect
    sessionStorage.setItem(INVITE_TOKEN_KEY, token);

    if (user) {
      // ✅ Already authenticated → go straight to the join form
      navigate(`/join/${token}`, { replace: true });
    } else {
      // 🔒 Not authenticated → Send to sign-up, token is in sessionStorage
      //    Also pass it explicitly in the query string as a belt-and-suspenders
      navigate(`/signup?invite=${token}`, { replace: true });
    }
  }, [token, user, loading, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
      <div className="flex flex-col items-center gap-3 text-gray-400">
        <Loader2 className="w-8 h-8 animate-spin" />
        <p className="text-sm">מעבד קישור הזמנה…</p>
      </div>
    </div>
  );
}
